import React, { useEffect, useState } from 'react';
import { ChefHat, Check, X, Download, Sparkles, Clock, Flame, ArrowRight, Star, Zap, ShieldCheck, Repeat } from 'lucide-react';
import { Footer } from './components/Footer';
import { Testimonials } from './components/Testimonials';
import { AboutCreator } from './components/AboutCreator';
import { ValueBreakdown } from './components/ValueBreakdown';
import { Comparison } from './components/Comparison';
import { TrustBadges } from './components/TrustBadges';
import { Guarantee } from './components/Guarantee';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { FAQ } from './components/FAQ';
import { FeaturedIn } from './components/FeaturedIn';
import { LanguageGate } from './components/LanguageGate';
import { PaymentMethods } from './components/PaymentMethods';
import { RecipeMenu } from './components/RecipeMenu';
import { EmailCapturePopup } from './components/EmailCapturePopup';
import { LiveActivity } from './components/LiveActivity';
import { SamplePreview } from './components/SamplePreview';
import { CountdownTimer } from './components/CountdownTimer';
import { initTikTokPixel, trackAddToCart } from './utils/tiktok';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
function MainApp() {
  const {
    t
  } = useLanguage();
  return <div className="w-full min-h-screen bg-[#FBF7F0]" data-id="element-0">
      {/* Hero Section */}
      <section className="w-full bg-[#F5F1E8] py-16 md:py-24" data-id="element-1">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center" data-id="element-3">
            <div className="order-2 lg:order-1 space-y-6" data-id="element-4">
              <div className="text-2xl md:text-3xl text-[#C2410C] leading-tight" style={{
              fontFamily: 'DM Serif Display, serif'
            }} data-id="element-5">
                {t('hero.eyebrow')}{' '}
                <span className="italic text-[#3E2723]" data-id="element-6">
                  {t('hero.eyebrowItalic')}
                </span>
              </div>
              <div className="inline-flex items-center gap-2 bg-[#C2410C] text-white px-4 py-2 rounded-full text-sm font-medium" data-id="element-7">
                <ChefHat className="w-4 h-4" data-id="element-8" />
                {t('hero.badge')}
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight text-[#3E2723]" data-id="element-9">
                {t('hero.h1')}
              </h1>
              <h2 className="text-lg sm:text-xl md:text-2xl text-[#3E2723]/80 font-normal" data-id="element-10">
                {t('hero.h2')}
              </h2>
              <div className="flex flex-wrap items-center gap-3 pt-2" data-id="element-11">
                <div className="flex gap-1" data-id="element-12">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-13" />)}
                </div>
                <span className="text-[#3E2723] font-bold" data-id="element-14">4.9/5</span>
                <span className="text-[#3E2723]/70" data-id="element-15">{t('hero.trusted')}</span>
              </div>
              <div className="pt-2" data-id="element-16">
                <a href="#offer" className="inline-flex items-center gap-2 bg-[#C2410C] hover:bg-[#9A3412] text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors shadow-lg" data-id="element-17">
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5" data-id="element-18" />
                </a>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-[#3E2723]/70" data-id="element-19">
                  <span className="inline-flex items-center gap-1.5" data-id="element-20">
                    <Zap className="w-4 h-4 text-[#14B8A6]" data-id="element-21" />
                    {t('hero.instant')}
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-22">
                    <ShieldCheck className="w-4 h-4 text-[#14B8A6]" data-id="element-23" />
                    {t('hero.guarantee')}
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-24">
                    <Repeat className="w-4 h-4 text-[#14B8A6]" data-id="element-25" />
                    {t('hero.lifetime')}
                  </span>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative w-full" data-id="element-26">
              <div className="absolute -inset-3 md:-inset-4 bg-[#C2410C]/15 rounded-3xl blur-2xl" data-id="element-27"></div>
              <img src="https://cdn.magicpatterns.com/uploads/rbo6hV3QCveJonYaYpaxSj/17.png" alt="Authentic Mexico: The Resort Secrets ebook shown on tablet and phone surrounded by guacamole, fish tacos, mole, and ceviche" loading="eager" className="relative block w-full h-auto object-cover rounded-2xl shadow-2xl border-4 border-white" data-id="element-28" />
            </div>
          </div>
        </div>
      </section>

      <RecipeMenu data-id="element-29" />
      <SamplePreview data-id="element-30" />

      {/* Opening Hook */}
      <section className="w-full py-16 md:py-24" data-id="element-31">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-32">
          <p className="text-2xl md:text-3xl leading-relaxed text-[#3E2723] text-center" data-id="element-33">
            {t('hook.p1')}
          </p>
          <p className="text-xl md:text-2xl leading-relaxed text-[#3E2723]/70 text-center mt-6" data-id="element-34">
            {t('hook.p2')}{' '}
            <span className="text-[#C2410C] font-semibold italic" data-id="element-35">
              {t('hook.wrong')}
            </span>
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="w-full py-16 md:py-24 bg-white" data-id="element-36">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-37">
          <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-8" data-id="element-38">
            {t('problem.title')}
          </h2>
          <p className="text-xl leading-relaxed text-[#3E2723]/80 mb-6" data-id="element-39">
            {t('problem.p1')}
          </p>
          <p className="text-xl leading-relaxed text-[#3E2723]/80" data-id="element-40">
            {t('problem.p2')}
          </p>
        </div>
      </section>

      {/* Why What You Tried Didn't Work */}
      <section className="w-full py-16 md:py-24" data-id="element-41">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-42">
          <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-12" data-id="element-43">
            {t('whyFailed.title')}
          </h2>
          <div className="space-y-6" data-id="element-44">
            {(t('whyFailed.items') as unknown as string[]).map((item, idx) => <div key={idx} className="flex gap-4 items-start" data-id="element-45">
                <X className="w-6 h-6 text-[#C2410C] flex-shrink-0 mt-1" data-id="element-46" />
                <p className="text-lg text-[#3E2723]/80" data-id="element-47">{item}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* The Unique Mechanism */}
      <section className="w-full py-16 md:py-24 bg-[#14B8A6]/5" data-id="element-48">
        <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-49">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-4 border-[#14B8A6]" data-id="element-50">
            <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-6" data-id="element-51">
              {t('mechanism.title')}
            </h2>
            <h3 className="text-3xl md:text-4xl text-[#14B8A6] mb-6 italic" data-id="element-52">
              {t('mechanism.subtitle')}
            </h3>
            <p className="text-xl leading-relaxed text-[#3E2723]/80" data-id="element-53">
              {t('mechanism.p1')}{' '}
              <span className="font-semibold text-[#C2410C]" data-id="element-54">
                {t('mechanism.highlight')}
              </span>
            </p>
          </div>
        </div>
      </section>

      <FeaturedIn data-id="element-55" />
      <AboutCreator data-id="element-56" />
      <ValueBreakdown data-id="element-57" />
      <Comparison data-id="element-58" />

      {/* Who This Is For */}
      <section className="w-full py-16 md:py-24" data-id="element-59">
        <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-60">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-61">
            <div data-id="element-62">
              <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-8" data-id="element-63">
                {t('whoFor.titleFor')}
              </h2>
              <div className="space-y-4" data-id="element-64">
                {(t('whoFor.itemsFor') as unknown as string[]).map((item, idx) => <div key={idx} className="flex gap-3 items-start" data-id="element-65">
                      <Check className="w-5 h-5 text-[#14B8A6] flex-shrink-0 mt-1" data-id="element-66" />
                      <p className="text-lg text-[#3E2723]/80" data-id="element-67">{item}</p>
                    </div>)}
              </div>
            </div>
            <div data-id="element-68">
              <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-8" data-id="element-69">
                {t('whoFor.titleNot')}
              </h2>
              <div className="space-y-4" data-id="element-70">
                {(t('whoFor.itemsNot') as unknown as string[]).map((item, idx) => <div key={idx} className="flex gap-3 items-start" data-id="element-71">
                      <X className="w-5 h-5 text-[#C2410C] flex-shrink-0 mt-1" data-id="element-72" />
                      <p className="text-lg text-[#3E2723]/80" data-id="element-73">{item}</p>
                    </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="w-full py-16 md:py-24 bg-[#EC4899]/5" data-id="element-74">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-75">
          <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-12 text-center" data-id="element-76">
            {t('howItWorks.title')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-77">
            {(t('howItWorks.steps') as unknown as {
            title: string;
            desc: string;
          }[]).map((item, idx) => <div key={idx} className="text-center" data-id="element-78">
                <div className="w-16 h-16 bg-[#EC4899] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-79">
                  {idx + 1}
                </div>
                <h3 className="text-2xl font-semibold text-[#3E2723] mb-3" data-id="element-80">
                  {item.title}
                </h3>
                <p className="text-[#3E2723]/70" data-id="element-81">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      <Testimonials data-id="element-82" />
      <Guarantee data-id="element-83" />
      <FAQ data-id="element-84" />
      <TrustBadges data-id="element-85" />

      {/* Offer + CTA */}
      <section id="offer" className="w-full min-h-screen flex items-center py-12 md:py-16 bg-[#3E2723]" data-id="element-86">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center w-full" data-id="element-87">
          <div className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-full text-sm md:text-base font-extrabold mb-6 animate-pulse shadow-[0_0_25px_rgba(220,38,38,0.7)] border-2 border-yellow-400 uppercase tracking-wide" data-id="element-88">
            <Flame className="w-5 h-5 text-yellow-300 animate-bounce" data-id="element-89" />
            <Clock className="w-4 h-4" data-id="element-90" />
            {t('offer.hurry')}
            <Flame className="w-5 h-5 text-yellow-300 animate-bounce" data-id="element-91" />
          </div>
          <CountdownTimer data-id="element-92" />
          <h2 className="text-4xl md:text-5xl text-white mb-4" data-id="element-93">
            {t('offer.title')}
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-6 max-w-2xl mx-auto" data-id="element-94">
            {t('offer.desc')}
          </p>

          <div className="flex justify-center mb-5" data-id="element-95">
            <div className="relative inline-flex items-center gap-3 bg-yellow-400 text-[#3E2723] px-6 py-3 rounded-xl font-extrabold text-base md:text-lg shadow-[0_0_30px_rgba(250,204,21,0.5)] border-2 border-yellow-300" data-id="element-96">
              <Sparkles className="w-5 h-5" data-id="element-97" />
              {t('offer.valued')}
              <Sparkles className="w-5 h-5" data-id="element-98" />
              <span className="absolute -top-2 -right-2 flex h-4 w-4" data-id="element-99">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EC4899] opacity-75" data-id="element-100"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-[#EC4899]" data-id="element-101"></span>
              </span>
            </div>
          </div>
          <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-5 inline-block border-2 border-[#EC4899]/50" data-id="element-102">
            <div className="absolute -top-3 -right-3 bg-yellow-400 text-[#3E2723] px-3 py-1 rounded-full text-xs font-extrabold rotate-12 shadow-lg flex items-center gap-1" data-id="element-103">
              <Sparkles className="w-3 h-3" data-id="element-104" />
              {t('offer.save')}
            </div>
            <div className="flex items-baseline justify-center gap-3 mb-1" data-id="element-105">
              <span className="text-2xl md:text-3xl text-white/50 line-through font-medium" data-id="element-106">
                $12.90
              </span>
              <span className="text-6xl md:text-7xl font-bold text-yellow-300" data-id="element-107">
                $6.39
              </span>
            </div>
            <div className="text-lg text-white/80" data-id="element-108">{t('offer.oneTime')}</div>
          </div>
          <div className="relative inline-block group" data-id="element-109">
            <span className="absolute inset-0 rounded-xl bg-yellow-300 blur-xl opacity-60 animate-pulse" data-id="element-110"></span>
            <a href="https://foundationalgut.lat/cart/45663549980774:1" onClick={() => trackAddToCart()} className="relative inline-flex items-center gap-3 bg-[#C2410C] hover:bg-[#9A3412] text-white px-10 py-5 rounded-xl text-lg md:text-2xl font-extrabold uppercase tracking-wide shadow-2xl border-4 border-yellow-300 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(250,204,21,0.8)]" data-id="element-111">
              <Download className="w-6 h-6 md:w-7 md:h-7" data-id="element-112" />
              <span data-id="element-113">{t('offer.cta')}</span>
              <ArrowRight className="w-6 h-6 md:w-7 md:h-7 group-hover:translate-x-1 transition-transform" data-id="element-114" />
            </a>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 mt-6 text-sm md:text-base text-white/80" data-id="element-115">
            <span className="inline-flex items-center gap-1.5" data-id="element-116">
              <Zap className="w-4 h-4 text-yellow-300" data-id="element-117" />
              {t('hero.instant')}
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-118">
              <ShieldCheck className="w-4 h-4 text-yellow-300" data-id="element-119" />
              {t('hero.guarantee')}
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-120">
              <Repeat className="w-4 h-4 text-yellow-300" data-id="element-121" />
              {t('hero.lifetime')}
            </span>
          </div>

          <PaymentMethods label={t('trustBadges.secure')} labelClassName="text-xs uppercase tracking-[0.25em] text-yellow-300 font-bold text-center mb-4" className="mt-8 max-w-xl mx-auto" data-id="element-122" />

          <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg leading-relaxed italic font-medium text-[#4ADE80]" data-id="element-123">
            {t('offer.coffee')}
          </p>

          <div className="mt-10 max-w-2xl mx-auto bg-white/5 border-l-4 border-yellow-300 rounded-r-lg p-6 text-left" data-id="element-124">
            <p className="text-white/90 text-base md:text-lg leading-relaxed" data-id="element-125">
              <span className="font-bold text-yellow-300" data-id="element-126">P.S. —</span>{' '}
              {t('offer.ps')}
            </p>
            <p className="text-white/70 text-sm mt-3 italic" data-id="element-127">
              — Isabella Romero
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-128" />
      <StickyMobileCTA data-id="element-129" />
      <LiveActivity data-id="element-130" />
      <EmailCapturePopup data-id="element-131" />
    </div>;
}
function AppWrapper() {
  const {
    lang,
    setLang
  } = useLanguage();
  if (!lang) {
    return <LanguageGate onSelect={setLang} data-id="element-132" />;
  }
  return <MainApp data-id="element-133" />;
}
export function App() {
  useEffect(() => {
    initTikTokPixel();
  }, []);
  return <LanguageProvider data-id="element-134">
      <AppWrapper data-id="element-135" />
    </LanguageProvider>;
}